"use client";

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, ChevronRight, Sparkles } from "lucide-react";
import { cn } from "@/utils/utils";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FILTER_CATEGORIES } from "../../config/filterFields";
import { FilterCategory, FilterFieldDefinition } from "../../types/audienceTypes";

// ============================================================================
// Component Props
// ============================================================================

interface FilterCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectField: (fieldId: string) => void;
}

// ============================================================================
// Category Icons
// ============================================================================

const CategoryIcons: Record<FilterCategory, string> = {
  contact: "👤",
  company: "🏢",
  subscription: "🔐",
  event: "🎫",
  learning: "📚",
  performance: "📞",
  financial: "💰",
  email_history: "📧",
};

// ============================================================================
// Main Component
// ============================================================================

export function FilterCategoryModal({
  isOpen,
  onClose,
  onSelectField,
}: FilterCategoryModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory | null>(null);

  // Filter categories and fields based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return FILTER_CATEGORIES;

    const term = searchTerm.toLowerCase();
    return FILTER_CATEGORIES.map((category) => ({
      ...category,
      fields: category.fields.filter(
        (field) =>
          field.label.toLowerCase().includes(term) ||
          field.description?.toLowerCase().includes(term)
      ),
    })).filter((category) => category.fields.length > 0);
  }, [searchTerm]);

  // Get fields for selected category
  const categoryFields = useMemo(() => {
    if (searchTerm) {
      // When searching, show all matching fields
      return filteredCategories.flatMap((c) => c.fields);
    }
    if (!selectedCategory) return [];
    const category = FILTER_CATEGORIES.find((c) => c.id === selectedCategory);
    return category?.fields || [];
  }, [selectedCategory, searchTerm, filteredCategories]);

  const handleSelectField = (fieldId: string) => {
    onSelectField(fieldId);
    setSearchTerm("");
    setSelectedCategory(null);
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedCategory(null);
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Add Filter
            </DialogTitle>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              autoFocus
              className="pl-10 h-10 bg-white border-gray-300"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value) setSelectedCategory(null);
              }}
              placeholder="Search for a property..."
              value={searchTerm}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex min-h-[400px] max-h-[60vh]">
          {/* Category List */}
          <div className="w-64 border-r border-gray-200 overflow-y-auto bg-gray-50">
            <div className="p-3 space-y-1">
              {FILTER_CATEGORIES.map((category) => {
                const matchingFields = searchTerm
                  ? category.fields.filter(
                      (f) =>
                        f.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        f.description?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                  : category.fields;

                const isDisabled = Boolean(searchTerm && matchingFields.length === 0);

                return (
                  <button
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      selectedCategory === category.id && !searchTerm
                        ? "bg-orange-50 border border-orange-200"
                        : "hover:bg-white border border-transparent",
                      isDisabled && "opacity-40 cursor-not-allowed"
                    )}
                    disabled={isDisabled}
                    key={category.id}
                    onClick={() => !isDisabled && setSelectedCategory(category.id)}
                  >
                    <span className="text-xl">{CategoryIcons[category.id]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            selectedCategory === category.id && !searchTerm
                              ? "text-orange-600"
                              : "text-gray-900"
                          )}
                        >
                          {category.label}
                        </span>
                        {searchTerm && matchingFields.length > 0 && (
                          <span className="text-xs text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                            {matchingFields.length}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 truncate block">
                        {category.fields.length} properties
                      </span>
                    </div>
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 text-gray-400",
                        selectedCategory === category.id && !searchTerm && "text-orange-600"
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Field List */}
          <div className="flex-1 overflow-y-auto bg-white">
            {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
            <AnimatePresence mode="wait">
              {categoryFields.length === 0 ? (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center p-8"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  key="empty"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    {searchTerm
                      ? "No matching properties found"
                      : "Select a category to see available properties"}
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="p-3 space-y-1"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  key={selectedCategory || "search"}
                >
                  {categoryFields.map((field, index) => (
                    <motion.button
                      animate={{ opacity: 1, x: 0 }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all group"
                      initial={{ opacity: 0, x: -10 }}
                      key={field.id}
                      onClick={() => handleSelectField(field.id)}
                      transition={{ delay: index * 0.02 }}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
                          {field.label}
                        </div>
                        {field.description && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {field.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded",
                            field.type === "text" && "bg-blue-100 text-blue-600",
                            field.type === "number" && "bg-green-100 text-green-600",
                            field.type === "date" && "bg-purple-100 text-purple-600",
                            field.type === "select" && "bg-orange-100 text-orange-600",
                            field.type === "multi_select" && "bg-pink-100 text-pink-600",
                            field.type === "boolean" && "bg-cyan-100 text-cyan-600"
                          )}
                        >
                          {field.type.replace("_", " ")}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FilterCategoryModal;
